import { useState, useEffect, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api, { imgUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Send, MessageSquare, ArrowLeft, Mail, Phone, Flag, ShieldCheck, CreditCard, Paperclip, Check, CheckCheck } from "lucide-react";
import ChatMedia from "@/components/ChatMedia";

const MAX_IMAGE = 10 * 1024 * 1024;
const MAX_VIDEO = 25 * 1024 * 1024;
const videoDuration = (file) => new Promise((resolve) => {
  const v = document.createElement("video");
  v.preload = "metadata";
  v.onloadedmetadata = () => { URL.revokeObjectURL(v.src); resolve(v.duration); };
  v.onerror = () => resolve(0);
  v.src = URL.createObjectURL(file);
});

const REPORT_REASONS = [
  "Ongepast of aanstootgevend gedrag",
  "Oplichting of verdachte betaling",
  "Spam of reclame",
  "Poging tot buiten platform om handelen",
  "Anders",
];

export default function Messages() {
  const { user } = useAuth();
  const [convs, setConvs] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [conversation, setConversation] = useState(null);
  const [sellerContact, setSellerContact] = useState(null);
  const [text, setText] = useState("");
  const [otherTyping, setOtherTyping] = useState(false);
  const typingSentRef = useRef(0);
  const [showReport, setShowReport] = useState(false);
  const [reportReason, setReportReason] = useState("");
  const [reportDetails, setReportDetails] = useState("");
  const [reporting, setReporting] = useState(false);
  const [showPay, setShowPay] = useState(false);
  const [payLink, setPayLink] = useState("");
  const [payAmount, setPayAmount] = useState("");
  const [params] = useSearchParams();
  const endRef = useRef(null);
  const fileRef = useRef(null);
  const [uploadPct, setUploadPct] = useState(null);

  const reportMedia = async (m) => {
    if (!window.confirm(`Deze ${m.media.type === "video" ? "video" : "foto"} melden als ongepast? Hij wordt voor jou verborgen en de beheerder bekijkt hem.`)) return;
    try {
      await api.post(`/messages/${m.id}/report-media`);
      setMessages((list) => list.map((x) => (x.id === m.id ? { ...x, media: undefined, media_hidden: true } : x)));
      toast.success("Bedankt, de beheerder bekijkt je melding");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kon niet melden");
    }
  };

  const sendMedia = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const isVideo = file.type.startsWith("video/");
    if (!isVideo && !file.type.startsWith("image/")) { toast.error("Alleen foto's en video's"); return; }
    if (file.size > (isVideo ? MAX_VIDEO : MAX_IMAGE)) { toast.error(isVideo ? "Video mag max 25 MB zijn" : "Foto mag max 10 MB zijn"); return; }
    if (isVideo && (await videoDuration(file)) > 31) { toast.error("Video mag max 30 seconden duren"); return; }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("caption", text.trim());
    setUploadPct(0);
    try {
      const { data } = await api.post(`/conversations/${activeId}/media`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (p) => p.total && setUploadPct(Math.round((p.loaded / p.total) * 100)),
      });
      setMessages((m) => [...m, data]);
      setText("");
      loadConvs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Versturen mislukt, probeer het opnieuw");
    } finally {
      setUploadPct(null);
    }
  };

  const loadConvs = async () => {
    const { data } = await api.get("/conversations");
    setConvs(data);
    const wanted = params.get("c");
    if (wanted && data.some((c) => c.id === wanted)) setActiveId(wanted);
    else if (!activeId && data.length) setActiveId(data[0].id);
  };

  useEffect(() => { loadConvs(); }, []);

  useEffect(() => {
    if (!activeId) return;
    setOtherTyping(false);
    api.get(`/conversations/${activeId}/messages`).then(({ data }) => {
      setMessages(data.messages);
      setConversation(data.conversation);
      setSellerContact(data.seller_contact || null);
      setOtherTyping(!!data.other_typing);
    });
    const interval = setInterval(() => {
      api.get(`/conversations/${activeId}/messages`).then(({ data }) => { setMessages(data.messages); setConversation(data.conversation); setOtherTyping(!!data.other_typing); }).catch(() => {});
    }, 2500);
    return () => clearInterval(interval);
  }, [activeId]);

  const onType = (e) => {
    setText(e.target.value);
    const now = Date.now();
    if (e.target.value && now - typingSentRef.current > 3000) {
      typingSentRef.current = now;
      api.post(`/conversations/${activeId}/typing`).catch(() => {});
    }
  };

  const stickRef = useRef(true);
  const scrollBottom = () => {
    const el = endRef.current?.parentElement;
    if (el && stickRef.current) el.scrollTop = el.scrollHeight;
  };
  useEffect(() => {
    const el = endRef.current?.parentElement;
    if (el) { stickRef.current = true; el.scrollTop = el.scrollHeight; }
  }, [messages.length]);

  const send = async () => {
    if (!text.trim()) return;
    const { data } = await api.post(`/conversations/${activeId}/reply`, { product_id: conversation.product_id, text });
    setMessages((m) => [...m, data]);
    setText("");
    loadConvs();
  };

  const sendPaymentRequest = async () => {
    const link = payLink.trim();
    if (!link) { toast.error("Vul een betaallink in (bijv. je Tikkie-link)"); return; }
    if (!/^https?:\/\//i.test(link)) { toast.error("De link moet met https:// beginnen"); return; }
    const amt = payAmount.trim() ? `€ ${Number(payAmount).toFixed(2)} ` : "";
    const body = `💳 Betaalverzoek ${amt}— je kunt veilig betalen via deze link: ${link}`;
    try {
      const { data } = await api.post(`/conversations/${activeId}/reply`, { product_id: conversation.product_id, text: body });
      setMessages((m) => [...m, data]);
      setShowPay(false); setPayLink(""); setPayAmount("");
      toast.success("Betaalverzoek verstuurd");
      loadConvs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kon betaalverzoek niet versturen");
    }
  };

  const submitReport = async () => {
    const reason = reportReason === "Anders" ? reportDetails.trim() : reportReason;
    if (!reason) { toast.error("Kies of vul een reden in"); return; }
    setReporting(true);
    try {
      await api.post(`/conversations/${activeId}/report`, { reason });
      toast.success("Bedankt, de melding is naar de beheerder gestuurd");
      setShowReport(false);
      setReportReason("");
      setReportDetails("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kon melding niet versturen");
    } finally {
      setReporting(false);
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-8">
      <h1 className="font-serif text-4xl font-semibold text-zinc-100 mb-6">Berichten</h1>
      {convs.length === 0 ? (
        <div data-testid="no-conversations" className="text-center py-24 rounded-xl border border-zinc-800 bg-zinc-900/40">
          <MessageSquare className="h-10 w-10 text-zinc-700 mx-auto mb-4" />
          <p className="font-serif text-xl text-zinc-300">Nog geen gesprekken</p>
          <p className="text-sm text-zinc-500 mt-2">Stuur een bericht naar een verkoper om te beginnen.</p>
          <Link to="/" className="inline-block mt-4 text-rose-400 hover:text-rose-300 text-sm">Bekijk advertenties</Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 grid-rows-[minmax(0,1fr)] gap-0 rounded-xl border border-zinc-800 overflow-hidden h-[70vh]">
          {/* Conversation list */}
          <div className={`border-r border-zinc-800 bg-zinc-950 min-h-0 overflow-y-auto ${activeId ? "hidden md:block" : "block"}`}>
            {convs.map((c) => (
              <button
                key={c.id}
                data-testid={`conversation-${c.id}`}
                onClick={() => setActiveId(c.id)}
                className={`w-full flex items-center gap-3 p-4 border-b border-zinc-800/60 text-left transition-colors ${activeId === c.id ? "bg-zinc-900" : "hover:bg-zinc-900/50"}`}
              >
                <div className="h-11 w-11 shrink-0 overflow-hidden rounded-lg bg-zinc-800">
                  {c.product_image ? <img src={imgUrl(c.product_image)} alt="" className="h-full w-full object-cover" /> : null}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-zinc-200 truncate">{c.other_name}</div>
                  <div className="text-xs text-zinc-500 truncate">{c.product_title}</div>
                  <div className="text-xs text-zinc-600 truncate mt-0.5">{c.last_message}</div>
                </div>
              </button>
            ))}
          </div>

          {/* Thread */}
          <div className={`md:col-span-2 min-h-0 overflow-hidden flex-col bg-zinc-950 ${activeId ? "flex" : "hidden md:flex"}`}>
            {conversation ? (
              <>
                <div className="flex items-center gap-3 border-b border-zinc-800 p-4">
                  <button className="md:hidden text-zinc-400" onClick={() => setActiveId(null)}><ArrowLeft className="h-5 w-5" /></button>
                  <div className="h-9 w-9 overflow-hidden rounded-lg bg-zinc-800">
                    {conversation.product_image ? <img src={imgUrl(conversation.product_image)} alt="" className="h-full w-full object-cover" /> : null}
                  </div>
                  <Link to={`/product/${conversation.product_id}`} className="min-w-0">
                    <div className="text-sm font-medium text-zinc-200 truncate hover:text-rose-300">{conversation.product_title}</div>
                    <div className="text-xs text-zinc-500">Gesprek</div>
                  </Link>
                  <button
                    data-testid="report-conversation-button"
                    onClick={() => setShowReport(true)}
                    title="Gesprek melden"
                    className="ml-auto flex items-center gap-1.5 rounded-full border border-zinc-800 px-3 py-1.5 text-xs text-zinc-400 hover:text-rose-300 hover:border-rose-900/60 transition-colors"
                  >
                    <Flag className="h-3.5 w-3.5" /> Meld
                  </button>
                </div>

                <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-3" onScroll={(e) => { const el = e.currentTarget; stickRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 120; }}>
                  <div data-testid="safe-pay-tips" className="rounded-lg border border-amber-800/40 bg-amber-950/20 p-3">
                    <div className="flex items-center gap-1.5 text-xs font-medium text-amber-300 mb-1.5">
                      <ShieldCheck className="h-3.5 w-3.5" /> Veilig betalen
                    </div>
                    <ul className="text-[11px] text-amber-200/80 space-y-0.5 list-disc pl-4">
                      <li>Betaal het liefst <strong>contant bij ophalen</strong> of via een <strong>iDEAL-betaalverzoek/Tikkie</strong>.</li>
                      <li>Maak <strong>nooit vooraf</strong> geld over aan iemand die je niet kent.</li>
                      <li>Deel geen inloggegevens of codes. Spreek af op een veilige, openbare plek.</li>
                    </ul>
                  </div>
                  {sellerContact && (sellerContact.email || sellerContact.phone) && (
                    <div data-testid="contact-reveal" className="rounded-lg border border-emerald-800/50 bg-emerald-950/20 p-3">
                      <div className="text-xs font-medium text-emerald-300 mb-1.5">Contactgegevens van {sellerContact.name} — jullie hebben contact 🎉</div>
                      <div className="flex flex-wrap gap-2">
                        {sellerContact.email && (
                          <a href={`mailto:${sellerContact.email}`} data-testid="reveal-email" className="flex items-center gap-1.5 rounded-md bg-zinc-900 border border-zinc-800 px-2.5 py-1.5 text-xs text-zinc-200 hover:text-emerald-300">
                            <Mail className="h-3.5 w-3.5" /> {sellerContact.email}
                          </a>
                        )}
                        {sellerContact.phone && (
                          <a href={`tel:${sellerContact.phone}`} data-testid="reveal-phone" className="flex items-center gap-1.5 rounded-md bg-zinc-900 border border-zinc-800 px-2.5 py-1.5 text-xs text-zinc-200 hover:text-emerald-300">
                            <Phone className="h-3.5 w-3.5" /> {sellerContact.phone}
                          </a>
                        )}
                      </div>
                    </div>
                  )}
                  {messages.map((m) => {
                    const mine = m.sender_id === user.id;
                    const otherId = conversation.buyer_id === user.id ? conversation.seller_id : conversation.buyer_id;
                    const readAt = conversation.last_read?.[otherId];
                    const read = !!readAt && readAt >= m.created_at;
                    return (
                      <div key={m.id} className={`flex flex-col ${mine ? "items-end" : "items-start"}`}>
                        <div data-testid={`message-${m.id}`} className={`max-w-[75%] rounded-2xl text-sm break-words ${m.media ? "p-1.5" : "px-4 py-2.5"} ${mine ? "bg-rose-600 text-white rounded-br-sm" : "bg-zinc-800 text-zinc-100 rounded-bl-sm"}`}>
                          {m.media && <ChatMedia media={m.media} mine={mine} testid={`chat-media-${m.id}`} onLoad={scrollBottom} />}
                          {m.media_removed && <div data-testid={`chat-media-removed-${m.id}`} className="px-2.5 py-1.5 text-xs italic opacity-70">Foto/video verwijderd door beheerder</div>}
                          {m.media_hidden && !m.media_removed && <div data-testid={`chat-media-hidden-${m.id}`} className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs italic opacity-70"><Flag className="h-3 w-3" /> Gemeld en verborgen</div>}
                          {m.text && <div className={m.media || m.media_removed || m.media_hidden ? "px-2.5 pt-1.5 pb-1" : ""}>{linkify(m.text, mine)}</div>}
                        </div>
                        {!mine && m.media && (
                          <button data-testid={`report-media-${m.id}`} onClick={() => reportMedia(m)} className="mt-1 flex items-center gap-1 text-[11px] text-zinc-500 hover:text-rose-300 transition-colors">
                            <Flag className="h-3 w-3" /> Meld {m.media.type === "video" ? "video" : "foto"}
                          </button>
                        )}
                        {mine && (
                          <span data-testid={`message-status-${m.id}`} data-read={read} title={read ? "Gelezen" : "Verstuurd"} className="mt-0.5 mr-1">
                            {read ? <CheckCheck className="h-4 w-4 text-sky-400" /> : <Check className="h-4 w-4 text-zinc-400" />}
                          </span>
                        )}
                      </div>
                    );
                  })}
                  <div ref={endRef} />
                </div>

                {otherTyping && (
                  <div data-testid="typing-indicator" className="flex items-center gap-2 px-4 pb-2 text-xs text-zinc-400 animate-fade-up">
                    <span className="flex gap-1 rounded-full bg-zinc-800 px-3 py-2">
                      {[0, 150, 300].map((d) => <span key={d} className="h-1.5 w-1.5 rounded-full bg-rose-400 animate-bounce" style={{ animationDelay: `${d}ms` }} />)}
                    </span>
                    {convs.find((c) => c.id === activeId)?.other_name || "De ander"} is aan het typen...
                  </div>
                )}
                {uploadPct !== null && (
                  <div data-testid="chat-upload-progress" className="border-t border-zinc-800 px-4 py-2">
                    <div className="flex justify-between text-[11px] text-zinc-400 mb-1"><span>Versturen...</span><span>{uploadPct}%</span></div>
                    <div className="h-1.5 rounded-full bg-zinc-800 overflow-hidden"><div className="h-full bg-rose-500 transition-[width]" style={{ width: `${uploadPct}%` }} /></div>
                  </div>
                )}
                <div className="border-t border-zinc-800 p-3 flex items-center gap-2">
                  <input ref={fileRef} type="file" accept="image/*,video/mp4,video/quicktime,video/webm" className="hidden" data-testid="chat-media-input" onChange={sendMedia} />
                  <button
                    data-testid="chat-attach-button"
                    onClick={() => fileRef.current?.click()}
                    disabled={uploadPct !== null}
                    title="Foto of video sturen"
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-zinc-700 bg-zinc-900 text-zinc-300 hover:text-rose-300 hover:border-rose-700 transition-colors disabled:opacity-50"
                  >
                    <Paperclip className="h-4 w-4" />
                  </button>
                  {conversation && user.id === conversation.seller_id && (
                    <button
                      data-testid="payment-request-button"
                      onClick={() => setShowPay(true)}
                      title="Betaalverzoek delen"
                      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-emerald-800/60 bg-emerald-950/30 text-emerald-300 hover:bg-emerald-950/60 transition-colors"
                    >
                      <CreditCard className="h-4 w-4" />
                    </button>
                  )}
                  <input
                    data-testid="message-reply-input"
                    value={text}
                    onChange={onType}
                    onKeyDown={(e) => e.key === "Enter" && send()}
                    placeholder="Typ een bericht..."
                    className="flex-1 rounded-full border border-zinc-800 bg-zinc-900 px-4 py-2.5 text-sm text-zinc-100 placeholder:text-zinc-500 focus:border-rose-700 focus:outline-none"
                  />
                  <button data-testid="message-send-button" onClick={send} className="flex h-10 w-10 items-center justify-center rounded-full bg-rose-600 hover:bg-rose-500 text-white transition-colors">
                    <Send className="h-4 w-4" />
                  </button>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-zinc-600">Selecteer een gesprek</div>
            )}
          </div>
        </div>
      )}

      {showReport && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setShowReport(false)}>
          <div data-testid="report-modal" className="w-full max-w-md rounded-2xl border border-zinc-800 bg-zinc-950 p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-1">
              <Flag className="h-4 w-4 text-rose-400" />
              <h2 className="font-serif text-xl font-semibold text-zinc-100">Gesprek melden</h2>
            </div>
            <p className="text-sm text-zinc-400 mb-4">Waarom wil je dit gesprek melden bij de beheerder?</p>
            <div className="space-y-2">
              {REPORT_REASONS.map((r) => (
                <button
                  key={r}
                  data-testid={`report-reason-${r}`}
                  onClick={() => setReportReason(r)}
                  className={`w-full text-left rounded-lg border px-3 py-2.5 text-sm transition-colors ${reportReason === r ? "border-rose-600 bg-rose-950/30 text-rose-200" : "border-zinc-800 bg-zinc-900/50 text-zinc-300 hover:border-zinc-700"}`}
                >
                  {r}
                </button>
              ))}
            </div>
            {reportReason === "Anders" && (
              <textarea
                data-testid="report-details-input"
                value={reportDetails}
                onChange={(e) => setReportDetails(e.target.value)}
                placeholder="Beschrijf kort wat er aan de hand is..."
                rows={3}
                className="mt-3 w-full rounded-lg border border-zinc-800 bg-zinc-900/60 px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-500 focus:border-rose-700 focus:outline-none"
              />
            )}
            <div className="mt-5 flex gap-3">
              <button
                data-testid="report-cancel-button"
                onClick={() => setShowReport(false)}
                className="flex-1 rounded-lg border border-zinc-800 px-4 py-2.5 text-sm text-zinc-300 hover:bg-zinc-900 transition-colors"
              >
                Annuleren
              </button>
              <button
                data-testid="report-submit-button"
                onClick={submitReport}
                disabled={reporting}
                className="flex-1 rounded-lg bg-rose-600 hover:bg-rose-500 px-4 py-2.5 text-sm font-medium text-white transition-colors disabled:opacity-60"
              >
                {reporting ? "Versturen..." : "Melden"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showPay && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setShowPay(false)}>
          <div data-testid="payment-modal" className="w-full max-w-md rounded-2xl border border-zinc-800 bg-zinc-950 p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-1">
              <CreditCard className="h-4 w-4 text-emerald-400" />
              <h2 className="font-serif text-xl font-semibold text-zinc-100">Betaalverzoek delen</h2>
            </div>
            <p className="text-sm text-zinc-400 mb-4">Plak je betaallink (bijv. je Tikkie- of iDEAL-betaalverzoek). De koper krijgt hem als klikbaar bericht.</p>
            <label className="block text-xs text-zinc-500 mb-1">Bedrag (optioneel)</label>
            <div className="relative mb-3">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">€</span>
              <input
                data-testid="payment-amount-input"
                type="number" step="0.01" min="0"
                value={payAmount}
                onChange={(e) => setPayAmount(e.target.value)}
                placeholder="0,00"
                className="w-full rounded-lg border border-zinc-800 bg-zinc-900 pl-8 pr-4 py-2.5 text-sm text-zinc-100 focus:border-emerald-700 focus:outline-none"
              />
            </div>
            <label className="block text-xs text-zinc-500 mb-1">Betaallink</label>
            <input
              data-testid="payment-link-input"
              value={payLink}
              onChange={(e) => setPayLink(e.target.value)}
              placeholder="https://tikkie.me/pay/..."
              className="w-full rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2.5 text-sm text-zinc-100 placeholder:text-zinc-500 focus:border-emerald-700 focus:outline-none"
            />
            <div className="mt-3 flex items-start gap-2 rounded-lg border border-amber-800/40 bg-amber-950/20 px-3 py-2 text-[11px] text-amber-200/80">
              <ShieldCheck className="h-4 w-4 shrink-0 mt-0.5 text-amber-300" />
              <span>Gebruik een officiële betaallink (Tikkie, ING, Rabo, bunq). Vraag nooit om inloggegevens of codes.</span>
            </div>
            <div className="mt-5 flex gap-3">
              <button data-testid="payment-cancel-button" onClick={() => setShowPay(false)} className="flex-1 rounded-lg border border-zinc-800 px-4 py-2.5 text-sm text-zinc-300 hover:bg-zinc-900 transition-colors">Annuleren</button>
              <button data-testid="payment-send-button" onClick={sendPaymentRequest} className="flex-1 rounded-lg bg-emerald-600 hover:bg-emerald-500 px-4 py-2.5 text-sm font-medium text-white transition-colors">Verstuur</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function linkify(text, mine) {
  if (!text) return text;
  const parts = String(text).split(/(https?:\/\/[^\s]+)/g);
  return parts.map((part, i) =>
    /^https?:\/\//i.test(part) ? (
      <a
        key={i}
        href={part}
        target="_blank"
        rel="noopener noreferrer"
        className={`underline break-all ${mine ? "text-white font-medium" : "text-emerald-300"}`}
      >
        {part}
      </a>
    ) : (
      part
    )
  );
}
